import { 
    ICommonAttributes, 
    IArtboardsAttributes, 
    IArtboardAttributes,
    IGroupAttributes,
    ITextAttributes,
    IImageAttributes,
    IShapeCommonAttributes,
    IShapeRectangleAttributes,
    IShapeCircleAttributes,
    IShapeTriangleAttributes,
    IShapeRhombusAttributes,
    IShapeLineAttributes,
    IShapeEllipseAttributes,
    IShapeStarAttributes,
    IElement, 
    IShapeCustomAttributes,
    IShapeKnot,
    IColorOverlay,
    IColorGradient,
    ITextStyle,
    IRawProps,
    IMaskData,
    IColorStop,
    ITextPartAttributes
        
} from '@routes/private/Editor/types';
import { pixelsLetterSpace } from '@utils/textMultiStyling';
import { getMidColor, getValueFromLinearFunction, rgbaToHex } from '@utils/userUtils';
import { sendFile } from '@utils/cloudStorage';
import { Preview } from '@state/templates/types';
import { IIncompatibleFontReplace} from 'routes/private/Templates/components/NewTemplate/types';
import { 
  Psd, 
  Layer, 
  LayerTextData, 
  LayerEffectsInfo, 
  TextStyleRun, 
  BezierKnot, 
  Color, 
  RGB, 
  EffectSolidGradient, 
  ExtraGradientInfo, 
  LayerEffectGradientOverlay,
  TextStyle,
  ParagraphStyle,
  OpacityStop,
} from 'ag-psd';
import { IDSConfig } from '@state/dsconfig/types';
import { findFontByNameOrId } from './fonts';
const uuid = require('react-uuid');

export interface IEffectColor {
  color: Color;
}

export interface IIsImage {
  image?: boolean;
}

export interface IShapeProps {
  isVector: boolean;
  backgroundColor?: string;
  bgGradient?: IColorGradient;
}

export const createPreviews = (canvas: HTMLCanvasElement | null, psdTree: Psd) => {
  const mainCtx = psdTree.canvas?.getContext('2d');
  let previews: Preview[] = [];
  if (canvas && psdTree.canvas) {
    if (psdTree.artboards && mainCtx) {
      psdTree.children?.filter(item => item.hidden === false).map(child => {
        const previewData = returnPreviewData(canvas, mainCtx, child);
        if (previewData) {
          let preview: Preview = {
            name: `${child.name}`,
            url: previewData
          }
          previews = [...previews, preview];
        }
      });
    } else {
      const width = psdTree.width;
      const height = psdTree.height;
      const ctx = canvas.getContext('2d');
      if (ctx && mainCtx) {
        var imageData = mainCtx.getImageData(0, 0, width, height);
        canvas.width = width;
        canvas.height = height;
        ctx.clearRect(0, 0, width, height);
        ctx.putImageData(imageData, 0 ,0);
        let preview: Preview = {
          name: ``,
          url: canvas.toDataURL()
        }
        previews = [...previews, preview];
      }
    }
  }
  return previews;
}

const returnPreviewData = (canvas: HTMLCanvasElement, mainCtx: CanvasRenderingContext2D, child: Layer) => {
  const ctx = canvas.getContext('2d');
  if (ctx && mainCtx && child.artboard?.rect) {
    const params = {
      left: child.artboard.rect.left,
      top: child.artboard.rect.top,
      width: child.artboard.rect.right - child.artboard.rect.left,
      height: child.artboard.rect.bottom - child.artboard.rect.top
    };
    var imageData = mainCtx.getImageData(params.left, params.top, params.width, params.height);
    canvas.width = params.width;
    canvas.height = params.height;
    ctx.clearRect(0, 0, params.width, params.height);
    ctx.putImageData(imageData, 0 ,0);
    return canvas.toDataURL();
  } else return false;
}

const CheckReplaceFont = (fontFamily: string, fonstReplace: IIncompatibleFontReplace[]) => {
  const index = fonstReplace.map((fontReplace) => { return fontReplace.incompatibleFont}).indexOf(fontFamily);
  if (index === -1 || fonstReplace[index].fontReplace === undefined) return fontFamily;
  else return fonstReplace[index].fontReplace;
}

const findStyleRunIndex = (attributes: ITextAttributes, textIndex: number): number => {
  let totalCount = 0;
  for (let i=0;i<attributes.parts.length;i++) {
    totalCount+=attributes.parts[i].length;
    if (textIndex < totalCount) {
      return i;
    }
  }
  return 0;
}

const checkBreakLines = (attributes: ITextAttributes): ITextAttributes => {
  attributes.text = attributes.text.replaceAll("\u0003","\n");
  return attributes;
}

const checkEmptyFirstLinesMovement = (attributes: ITextAttributes): ITextAttributes => {
  let linesMove = 0;
  let breakLinePositions: number[] = [];
  let i = 0;
  const pixelsSpace = pixelsLetterSpace;
  while (attributes.text.length > i && (attributes.text.substring(i,i+1) === "\n" || attributes.text.substring(i,i+1) === " ")) {
    if (attributes.text.substring(i,i+1) === "\n") {
      linesMove++;
      breakLinePositions.push(i);
    }
    i++;
  }
  if (linesMove > 0) {
    let yMove = 0;
    for (let i=0;i<breakLinePositions.length;i++) {
      const partIndex = findStyleRunIndex(attributes,breakLinePositions[i]);
      let move: number = attributes.style.fontSize??0;
      if (attributes.parts[partIndex].style && attributes.parts[partIndex].style.fontSize) {
        move = attributes.parts[partIndex].style.fontSize!;
      }
      yMove += (move*pixelsSpace); 
    }
    attributes.y -= yMove;
  }
  return attributes;
}

const checkTextAttributes = (attributes: ITextAttributes): ITextAttributes => {
  attributes = checkBreakLines(attributes);
  return checkEmptyFirstLinesMovement(attributes);
} 

export const cleanCanvas = (object: Layer & Psd & IIsImage, fonstReplace: IIncompatibleFontReplace[], parentEffects?: LayerEffectsInfo) => {
    const shapeProps = getShapeProps(object);
    const element: IElement = {
        id: `${object.id}`,
        name: `${object.name}`,
        type: (object.artboards || (object.children && object.children.length>0 && object.children[0].artboard)) ? 'Artboards' : 
        object.artboard ? 'Artboard' : 
        object.text ? 'Text' : 
        shapeProps.isVector ? 'Shape':
        object.image ? 'Image' :'Group',        
        children: object.children?.map(child => cleanCanvas(child as Layer & Psd & IIsImage, fonstReplace, object.effects ? object.effects: parentEffects)),
        attributes: getCommonAttributes(object)
    }
    if (element.type === "Artboards" && object.artboards) {
        let attributes: IArtboardsAttributes = {
        ...element.attributes,
        offsetX: object.artboards?.origin ? object.artboards.origin.horizontal : 0,
        offsetY: object.artboards?.origin ? object.artboards.origin.vertical : 0,
        backgroundColor: rgbaToHex(object.artboards.docDefaultNewArtboardBackgroundColor) ?? "#FFFFFF"
        }
        element.attributes = attributes;
    } else if (element.type === "Artboard" && object.artboard?.rect) {
        let attributes: IArtboardAttributes = {
        ...element.attributes,
        x: object.artboard.rect.left,
        y: object.artboard.rect.top,
        width: object.artboard.rect.right - object.artboard.rect.left,
        height: object.artboard.rect.bottom - object.artboard.rect.top,
        backgroundColor: rgbaToHex(object.artboard.color) ?? "#FFFFFF"
        }
        element.attributes = attributes;
    } else if (element.type === "Text" && object.text) {
      let attributes: ITextAttributes = {
        ...element.attributes,        
        text: object.text.text,
        textAlign: object.text.paragraphStyle?.justification,        
        style: getTextStyle(object.text.style, object.text.paragraphStyle, true, fonstReplace, object.text.transform ? object.text.transform[3] : undefined, parentEffects),
        parts: getTextParts(object.text, fonstReplace, object.text.transform ? object.text.transform[3] : undefined, parentEffects),
        }
        element.attributes = checkTextAttributes(attributes);
    } else if (element.type === "Image") {
        let attributes: IImageAttributes = {
            ...element.attributes,
            srcImage: element.id,
            image: {
                x: element.attributes.x,
                y: element.attributes.y,
                width: element.attributes.width,
                height: element.attributes.height,
            },
            colorOverlay:getColorOverlay(object.effects)
        }
        element.attributes = attributes;
    } else if (element.type === 'Shape' && object.vectorMask){
      let attributes: IShapeCustomAttributes ={
        ...element.attributes,
        srcImage: element.id,
        initX: object.left,
        initY: object.top,
        initWidth: element.attributes.width,
        initHeight: element.attributes.height,
        shapeType: "Custom",        
        backgroundColor: shapeProps.backgroundColor,
        bgGradient: shapeProps.bgGradient,
        knots: getVectorKnots(object.vectorMask.paths[0].knots)
      }                
      element.attributes = attributes;
    }
    return element;
}

interface IRange {
  start: number;
  end: number
}

const getTextParts = (object: LayerTextData, fonstReplace: IIncompatibleFontReplace[], fontTranform?:number, parentEffects?: LayerEffectsInfo) : ITextPartAttributes[] =>{
  let textParts: ITextPartAttributes[] = [];
  if (object && object.styleRuns)
  {
    textParts = object.styleRuns.map(part => {
      return{
        length: part.length, 
        style: getTextStyle(part.style, object.paragraphStyle, false,fonstReplace,fontTranform,parentEffects)
      }
    });
  }
  else {
    textParts = [{length: object.text.length, style: getTextStyle(object.style, object.paragraphStyle, false,fonstReplace,fontTranform,parentEffects)}];
  }
  return textParts
}

const getTextStyle = (style: TextStyle|undefined, paragraphStyle: ParagraphStyle|undefined, main:boolean, fonstReplace: IIncompatibleFontReplace[], fontTranform?:number, parentEffects?: LayerEffectsInfo) : ITextStyle =>{

  let parameters: ITextStyle = {};
  if (style)
  {

    let fontSize : number | undefined = (style.fontSize)? (style.fontSize * (fontTranform??1)):undefined;

    let fillColor: string | undefined = undefined;
    let fillColorObject = undefined;
    if (style.fillColor)
    {
      fillColorObject = style.fillColor;
      if (fillColorObject !== undefined) 
        fillColor = rgbaToHex(fillColorObject);      
    }

    //color from Effect
    if (parentEffects?.solidFill?.length && parentEffects.solidFill.length > 0 && parentEffects?.solidFill[0]?.enabled === true) {
      fillColorObject = parentEffects.solidFill[0].color
      if (fillColorObject !== undefined) 
        fillColor = rgbaToHex(fillColorObject);      
    }

    if (style.font)  parameters.fontFamily = CheckReplaceFont(style.font.name, fonstReplace);
    if (fontSize) parameters.fontSize = fontSize;
    if (fillColor) parameters.color = fillColor;
    if (style.fontCaps) parameters.fontUpperCase = style.fontCaps;
    if (style.fontBaseline === 1) parameters.superscript = true;
    if (style.fontBaseline === 2) parameters.subscript = true;

    let lineHeight = undefined; 
    if (style?.autoLeading) {
      if (paragraphStyle?.autoLeading) {
        lineHeight = paragraphStyle.autoLeading;
      }
    }
    else if (style?.leading!==undefined && style.fontSize!==undefined && style.fontSize !== 0) {
      lineHeight = (style!.leading!/style!.fontSize!);
    }
    parameters.lineHeight = lineHeight;
    
    if (main) {
      parameters.fontItalic = false;
      parameters.fontWeight = false;
      parameters.fontUnderline = false;
    }

    if (style.fauxBold) parameters.fontWeight = style.fauxBold; 
    if (style.fauxItalic) parameters.fontItalic = style.fauxItalic; 
    if (style.underline) parameters.fontUnderline = style.underline; 

    if (style.tracking) parameters.letterSpacing = style.tracking;
  }
  return parameters;
}

const getShapeProps = (object: Layer): IShapeProps =>{
  let shapeProps: IShapeProps = { isVector: false };
  if (!object) return shapeProps;
  const layerEffectGradient = object.effects?.gradientOverlay?.length === 1 ? object.effects.gradientOverlay[0] as LayerEffectGradientOverlay & ExtraGradientInfo  : undefined;
  const vFill = object.vectorFill as (EffectSolidGradient & ExtraGradientInfo & IEffectColor )| undefined;
  const gradient = layerEffectGradient?.enabled && layerEffectGradient?.gradient ? layerEffectGradient.gradient as EffectSolidGradient : undefined;
  const colorStops = getColorStops(gradient ?? vFill);
  if (colorStops.length && (layerEffectGradient?.type === 'linear' || layerEffectGradient?.type === 'radial' || vFill?.style === 'linear' || vFill?.style === 'radial')) {
    shapeProps.bgGradient = { 
      hasGradient: true,
      angle: gradient && layerEffectGradient?.angle ? layerEffectGradient.angle : vFill?.angle,
      style: gradient && layerEffectGradient?.type ? layerEffectGradient.type as any : vFill?.style,
      scale: gradient && layerEffectGradient?.scale ? layerEffectGradient.scale : vFill?.scale,
      offset: gradient && layerEffectGradient?.offset ? layerEffectGradient.offset : vFill?.offset,
      colorStops,
    }
  } else {
    shapeProps.backgroundColor = rgbaToHex(vFill?.color);
  }
  if (shapeProps.bgGradient || shapeProps.backgroundColor) shapeProps.isVector = true;
  return shapeProps;
}

const getColorOverlay =(object: LayerEffectsInfo | undefined): IColorOverlay | undefined=>{
  if (object?.solidFill?.length)
  {
    if (object.solidFill.length > 0 && object.solidFill[0].enabled === true) {
        const color = object.solidFill[0].color as RGB;
        const alpha = object.solidFill[0].opacity ?? 1;
        return {
          red:color.r,
          green: color.g,
          blue: color.b,
          alpha:alpha        }
    }
  }
  return undefined
}

const getColorStops = (gradient?: EffectSolidGradient) => {
  let colorStops: IColorStop[] = [];
  if (gradient?.opacityStops?.length) {
    for (let i = 0; i < gradient.colorStops.length; i++) {
      const colorStop = gradient.colorStops[i];
      if (i > 0 && colorStop.midpoint !== 0.5) {
        const prevColorStop = gradient.colorStops[i - 1];
        let location = prevColorStop.location + (colorStop.location - prevColorStop.location) * colorStop.midpoint;
        let color = getMidColor(prevColorStop.color as RGB, colorStop.color as RGB, colorStop.midpoint);
        colorStops.push({
          color: rgbaToHex(color) ?? "#FFFFFF",
          location: location,
          opacity: getOpacity(location, gradient?.opacityStops),
        });
      }
      colorStops.push({
        color: rgbaToHex(colorStop.color) ?? "#FFFFFF",
        location: colorStop.location,
        opacity: getOpacity(colorStop.location, gradient?.opacityStops),
      });
    }
  }
  return colorStops;
}

const getOpacity = (location: number, opacityStops?: OpacityStop[]) => {
  if (opacityStops?.length) {
    for (let i = 0; i < opacityStops.length; i ++) {
      const opacityStop = opacityStops[i];
      if (opacityStop.location >= location) {
        if (i > 0) {
          const prevOpacityStop = opacityStops[i - 1];
          return getValueFromLinearFunction(prevOpacityStop.location, prevOpacityStop.opacity, opacityStop.location, opacityStop.opacity, location);
        } else
          return getValueFromLinearFunction(0, 1, opacityStop.location, opacityStop.opacity, location);
      }
    }
  }
  return 1;
}

const getVectorKnots= (object: BezierKnot[]): IShapeKnot[]=>{
  if (object)
  {
  const shapeKnots: IShapeKnot[] = object.map(knot =>{
    return{
      linked: knot.linked,
      points: knot.points
    }
  })
  return shapeKnots
  }
  return []

}

const getCommonAttributes = (object: Layer & Psd): ICommonAttributes => {
    let width: number|undefined = object.width ?? (object.right!==undefined && object.left!==undefined ? object.right - object.left : undefined) ;
    let height = object.height ?? (object.bottom!==undefined && object.top!==undefined ? object.bottom - object.top : undefined);
    let posx = object.width ? 0 : object.left ?? 0;
    let posy = object.height ? 0 : object.top ?? 0;

    if (object.text?.transform)
    {
        if (object.text.boxBounds)
        {
            width = object.text.boxBounds[2] * object.text.transform[0];        
            posx = object.text.transform[4];
            posy = object.text.transform[5];
        }
        else 
            width = undefined;
    }
    let rawProps: IRawProps[] | undefined = undefined;
    if (object.mask || object.effects || object.vectorFill) {
      let rawProp: IRawProps = {};
      if (object.mask) rawProp.mask = object.mask as IMaskData;
      if (object.effects) rawProp.effects = object.effects;
      if (object.vectorFill) rawProp.vector = {vectorFill: object.vectorFill, vectorMask: object.vectorMask};
      rawProps = [rawProp];
    }

    return {
        x: posx,
        y: posy,
        width: width,
        height: height,
        hidden: object.hidden ?? false,
        opacity: object.opacity,
        clipping: object.clipping,
        blendMode: object.blendMode,
        rawProps: rawProps,
    }
}

export const addColorToPalette = (colorObj: Color, colorsPalette: string[]) : string[] => {
    const colors = colorsPalette;
    const color = rgbaToHex(colorObj);
    if (color) {
      const fillColorIndex = colors.findIndex((c: string) => (c === color));
      if (fillColorIndex === -1) colors.push(color);
    }
    return colors;
}

export const getFontProps = (dsConfig: IDSConfig, textProps: LayerTextData) => {
    let fillColors: string[] = [];
    let incompatibleChildFonts: string[] = [];
    if (textProps.style?.font?.name) {
      const name = textProps.style.font.name;
      const font = findFontByNameOrId(dsConfig.brands, "name", name);
      if (font) textProps.style.font.name = font.id
      else incompatibleChildFonts = [...incompatibleChildFonts, name];
    }
    if (textProps.style?.fillColor) fillColors = [...fillColors, rgbaToHex(textProps.style.fillColor) ?? ""];
    if (textProps.styleRuns && textProps.styleRuns.length > 0) {
      textProps.styleRuns.map(styleRun => {
        if (styleRun.style) {
            if (styleRun.style.fillColor) fillColors = [...fillColors, rgbaToHex(styleRun.style.fillColor) ?? ""];
            if (styleRun.style.font?.name) {
              const name = styleRun.style.font.name;
              const font = findFontByNameOrId(dsConfig.brands, "name", name);
              if (font) styleRun.style.font.name = font.id
              else incompatibleChildFonts = [...incompatibleChildFonts, name];
            }
        }
      });
    }
    return {fillColors, incompatibleChildFonts};
}

